import React, { useState, useEffect, useCallback, useRef } from 'react';
import LookEditor from './LookEditor';
import FavoritesLayoutEditor from './FavoritesLayout';
import ManageLooks from './ManageLooks';
import { fetchLooks, fetchLookPurchaseCounts, updateLook } from './shared/api';
import generateThumbnail from './shared/generateThumbnail';
import { FullPageSpinner } from './shared/Spinner';

// Error boundary to prevent white screens from uncaught render errors
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('FavoritesDesigner error:', error, info); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 16, color: '#d32f2f', marginBottom: 12 }}>Something went wrong.</div>
          <div style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>{this.state.error.message}</div>
          <button
            onClick={() => this.setState({ error: null })}
            style={{ padding: '8px 24px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const TABS = [
  { id: 'layout', label: 'Favorites Layout' },
  { id: 'editor', label: 'Look Editor' },
  { id: 'manage', label: 'Manage Looks' }
];

const FavoritesDesigner = () => {
  const [activeTab, setActiveTab] = useState('layout');
  const [looks, setLooks] = useState([]);
  const [editingLook, setEditingLook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [childDirty, setChildDirty] = useState(false);
  const [referencePoses, setReferencePoses] = useState([]);
  const [purchaseCounts, setPurchaseCounts] = useState({});
  const dirtyRef = useRef(false);
  // Track which tabs have been visited so we can lazy-mount them
  const [mountedTabs, setMountedTabs] = useState({ layout: true });

  // Keep ref in sync for beforeunload handler
  useEffect(() => {
    dirtyRef.current = childDirty;
  }, [childDirty]);

  // Browser beforeunload warning
  useEffect(() => {
    const handler = (e) => {
      if (dirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const loadLooks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchLooks();
      setLooks(data);

      // Auto-regenerate thumbnails that are missing or are stale JPEGs (which cause black backgrounds).
      // A JPEG ref_photo_thumb starts with "data:image/jpeg" — those need to be replaced with PNG.
      const needsThumb = data.filter(l => {
        if (!l.ref_photo_data && !l.ref_photo_thumb) return false; // no source data at all
        if (!l.ref_photo_thumb) return !!l.ref_photo_data; // missing thumb, has source
        if (l.ref_photo_thumb.startsWith('data:image/jpeg')) return !!(l.ref_photo_data || l.ref_photo_thumb); // stale JPEG
        return false;
      });

      if (needsThumb.length > 0) {
        const source = (look) => look.ref_photo_data || look.ref_photo_thumb;
        Promise.all(needsThumb.map(async (look) => {
          try {
            const thumb = await generateThumbnail(source(look));
            if (!thumb) return null;
            await updateLook(look.id, { ref_photo_thumb: thumb });
            return { id: look.id, ref_photo_thumb: thumb };
          } catch (err) {
            console.error(`Failed to regenerate thumbnail for look ${look.id}:`, err);
            return null;
          }
        })).then(results => {
          const updated = results.filter(Boolean);
          if (updated.length > 0) {
            setLooks(prev => prev.map(l => {
              const u = updated.find(r => r.id === l.id);
              return u ? { ...l, ref_photo_thumb: u.ref_photo_thumb } : l;
            }));
          }
        });
      }
    } catch (err) {
      console.error('Failed to load looks:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadLooks(); }, [loadLooks]);

  const loadPurchaseCounts = useCallback(async () => {
    try {
      const counts = await fetchLookPurchaseCounts();
      setPurchaseCounts(counts || {});
    } catch (err) {
      console.error('Failed to load purchase counts:', err);
    }
  }, []);

  // Only fetch purchase counts when the Manage Looks tab is active
  useEffect(() => {
    if (activeTab !== 'manage') return;
    loadPurchaseCounts();
    const interval = setInterval(loadPurchaseCounts, 60000);
    return () => clearInterval(interval);
  }, [activeTab, loadPurchaseCounts]);

  // Guarded tab switch: confirm if dirty, lazy-mount on first visit
  const switchTab = useCallback((tabId) => {
    if (childDirty && !window.confirm('You have unsaved changes. If you leave, your changes will be lost.')) {
      return;
    }
    setMountedTabs(prev => prev[tabId] ? prev : { ...prev, [tabId]: true });
    setActiveTab(tabId);
  }, [childDirty]);

  const handleEditLook = (look) => {
    if (childDirty && !window.confirm('You have unsaved changes. If you leave, your changes will be lost.')) {
      return;
    }
    setEditingLook(look);
    setMountedTabs(prev => prev.editor ? prev : { ...prev, editor: true });
    setActiveTab('editor');
  };

  const handleNewLook = () => {
    if (childDirty && !window.confirm('You have unsaved changes. If you leave, your changes will be lost.')) {
      return;
    }
    setEditingLook(null);
    setMountedTabs(prev => prev.editor ? prev : { ...prev, editor: true });
    setActiveTab('editor');
  };

  const handleLookSaved = (savedLook, isNew) => {
    setLooks(prev => {
      const idx = prev.findIndex(l => l.id === savedLook.id);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = savedLook;
        return updated;
      }
      return [...prev, savedLook];
    });

    setChildDirty(false);

    if (isNew) {
      // New look → go to Favorites Layout tab to assign it to slots
      setEditingLook(savedLook);
      setMountedTabs(prev => prev.layout ? prev : { ...prev, layout: true });
      setActiveTab('layout');
    } else {
      // Existing look updated → go back to Manage Looks tab
      setEditingLook(null);
      setMountedTabs(prev => prev.manage ? prev : { ...prev, manage: true });
      setActiveTab('manage');
    }
  };

  const handleLookDeleted = (id) => {
    setLooks(prev => prev.filter(l => l.id !== id));
    if (editingLook?.id === id) {
      setEditingLook(null);
      setActiveTab('manage');
    }
  };

  const handleDirtyChange = useCallback((isDirty) => {
    setChildDirty(isDirty);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Tab bar */}
      <div style={{
        display: 'flex', borderBottom: '2px solid #e0e0e0', background: '#fafafa',
        padding: '0 16px'
      }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => switchTab(tab.id)}
            style={{
              padding: '12px 24px',
              border: 'none',
              borderBottom: activeTab === tab.id ? '3px solid #1976d2' : '3px solid transparent',
              background: 'none',
              color: activeTab === tab.id ? '#1976d2' : '#666',
              fontWeight: activeTab === tab.id ? 600 : 400,
              fontSize: 14,
              cursor: 'pointer',
              marginBottom: -2
            }}
          >
            {tab.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        {activeTab !== 'editor' && (
          <button
            onClick={handleNewLook}
            style={{
              padding: '8px 16px', margin: '6px 0',
              background: '#1976d2', color: '#fff', border: 'none',
              borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 500
            }}
          >
            + New Look
          </button>
        )}
      </div>

      {/* Tab content — all panels stay mounted, hidden via CSS to preserve state */}
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {loading && (
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(255,255,255,0.7)', zIndex: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <FullPageSpinner label="Loading looks..." />
          </div>
        )}
        <ErrorBoundary>
          {mountedTabs.layout && (
            <div style={{ display: activeTab === 'layout' ? 'flex' : 'none', height: '100%', flexDirection: 'column' }}>
              <FavoritesLayoutEditor looks={looks} loading={loading} onDirtyChange={handleDirtyChange} onReferencePosesChange={setReferencePoses} />
            </div>
          )}
          {mountedTabs.editor && (
            <div style={{ display: activeTab === 'editor' ? 'flex' : 'none', height: '100%' }}>
              <LookEditor
                look={editingLook}
                looks={looks}
                referencePoses={referencePoses}
                onSaved={handleLookSaved}
                onBack={() => switchTab('manage')}
                onDirtyChange={handleDirtyChange}
              />
            </div>
          )}
          {mountedTabs.manage && (
            <div style={{ display: activeTab === 'manage' ? 'block' : 'none', height: '100%', overflow: 'auto' }}>
              <ManageLooks
                looks={looks}
                loading={loading}
                onEdit={handleEditLook}
                onDeleted={handleLookDeleted}
                onNew={handleNewLook}
                purchaseCounts={purchaseCounts}
              />
            </div>
          )}
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default FavoritesDesigner;
